import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Trans, withTranslation } from 'react-i18next';
import moment from 'moment';
import cn from 'classnames';
import { withBrand } from '../../../providers/BrandProvider';
import { DeliveryType } from '../../../constants';
import {
  getCorrectLocale,
  renderCSSString,
  renderHTMLString,
} from '../../../helpers';
import { getDeliveryTypeName } from '../../../helpers/getDeliveryTypeName';
import { getTicketCount } from '../../../store/pricingDucks/chosenProducts';
import { FormBoxInput } from '../../shared/forms';
import Link from '../../shared/Link/Link';
import Button from '../../shared/Button/Button';

import './SelectCourseOptionForm.scss';

export class SelectCourseOptionForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      optionIndex: 1,
    };
  }

  getLocation = (location) => {
    const venueName = location.venueName || '';
    const cityName = location.city || location.cityAsText || '';
    const country = location.country || '';
    return `${venueName}${venueName && cityName ? ', ' + cityName : cityName}${
      (venueName || cityName) && country ? ', ' + country : country
    }`;
  };

  getPeriod = (startDate, endDate) => {
    const { i18n } = this.props;
    const datePattern = 'YYYY-MM-DD'; // date pattern from BE
    const startDateObj = startDate ? moment(startDate, [datePattern]) : null;
    const endDateObj = endDate ? moment(endDate, [datePattern]) : null;

    let formattedStartDate = 'DD MMM YYYY';

    const momentLocale = getCorrectLocale(i18n.language);

    let optionStarts =
      startDateObj && moment.isMoment(startDateObj)
        ? startDateObj.locale(momentLocale).format(formattedStartDate)
        : '';
    const optionEnds =
      endDateObj && moment.isMoment(endDateObj)
        ? endDateObj.locale(momentLocale).format('DD MMM YYYY')
        : '';

    if (optionStarts && optionEnds) {
      const sameMonth = startDateObj.format('M') === endDateObj.format('M');
      const sameYear = startDateObj.format('Y') === endDateObj.format('Y');
      formattedStartDate = `DD${
        !sameYear ? ' MMM YYYY' : !sameMonth ? ' MMM' : ''
      }`;

      optionStarts = `${startDateObj.format(formattedStartDate)} - `;
    }

    return `${optionStarts}${optionEnds}`;
  };

  getOptionDate = (option = {}) => {
    const {
      startDate,
      endDate,
      dateAsText,
      deliveryType = DeliveryType.ON_DEMAND,
    } = option;

    if (startDate) {
      return this.getPeriod(startDate, endDate);
    }

    if (dateAsText) {
      return dateAsText;
    }

    return getDeliveryTypeName(deliveryType);
  };

  render() {
    const {
      courseOptions = {},
      description,
      brand: { secondaryColor } = {},
      t,
      ticketCount,
      deliveryType,
      isMultiCurrency,
      pageConfig: { location } = {},
    } = this.props;

    let { optionIndex = false } = this.state;
    const enabledOptions = courseOptions.filter((opt) => opt.enabled);
    let selectedOption = null;

    if (
      enabledOptions.length === 1 &&
      courseOptions.length > enabledOptions.length
    ) {
      selectedOption = enabledOptions[0];
      selectedOption.selected = true;
      optionIndex = selectedOption.index;
    }

    const continueLink =
      ticketCount && deliveryType === 'DISTANCE'
        ? '/purchase/view-basket'
        : `/purchase/course-option/${optionIndex}`;

    return (
      <section className="c-select-course-option-form">
        {renderCSSString(`
          .c-form-box-input__box::before {
            border-color: ${secondaryColor};
          }
          .c-form-box-input--radio input:checked + .c-form-box-input__box::before {
            background-color: ${secondaryColor};
          }
          .c-form-box-input--checkbox input:checked + .c-form-box-input__box::before {
            background-color: ${secondaryColor};
          }
        `)}
        <div className="container">
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <div className="c-select-course-option-form__page-content">
                {renderHTMLString(description, location)}
              </div>
            </div>
            <div className="col-xs-12 col-sm-6">
              <div
                className="c-select-course-option-form__heading uppercase"
                style={{ color: secondaryColor }}
              >
                {deliveryType === 'DISTANCE' && isMultiCurrency
                  ? 'Select date and desired currency'
                  : t('site.purchase.select-option-title')}
              </div>

              {selectedOption && (
                <Fragment>
                  <div className="expanded-box-content">
                    <Trans
                      i18nKey="site.purchase.change-date-message"
                      values={{
                        0: this.getOptionDate(selectedOption),
                      }}
                    />
                    <div className="clear-basket-btn">
                      <Button
                        look="accent"
                        size="small"
                        onClick={this.props.onClearBasket}
                        className="c-select-course-option-form__clear-basket"
                      >
                        {t('site.purchase.clear-order')}
                      </Button>
                    </div>
                  </div>
                </Fragment>
              )}

              {courseOptions.length > 0 && (
                <>
                  {courseOptions.map((option) => (
                    <div
                      key={option.index}
                      className={cn({
                        'c-select-course-option-form__option': true,
                        'selected-option': optionIndex === option.index,
                      })}
                    >
                      <FormBoxInput
                        type="radio"
                        value={option.index}
                        checked={optionIndex === option.index}
                        disabled={!option.enabled}
                        onChange={() => {
                          this.setState({
                            optionIndex: option.index,
                          });
                        }}
                        color={secondaryColor}
                      >
                        <div className="row">
                          <div className="col-xs-12 col-sm-7">
                            <div className="c-select-course-option-form__option-label">
                              {this.getOptionDate(option)}
                            </div>
                            <div className="c-select-course-option-form__option-location">
                              {this.getLocation(option.courseLocation)}
                            </div>
                          </div>
                          <div className="col-xs-12 col-sm-5">
                            <div className="c-select-course-option-form__option-price">
                              {option.firstPrimaryProductPrice}
                            </div>
                          </div>
                        </div>
                        {option.tier.maxSaving && option.tier.endDate && (
                          <div className="c-select-course-option-form__option-saving">
                            <Trans
                              i18nKey="event.purchase.save-by-date"
                              values={{
                                0: option.tier.maxSaving,
                                1: option.tier.endDate,
                              }}
                            />
                          </div>
                        )}
                      </FormBoxInput>
                    </div>
                  ))}
                  <div className="row">
                    <div className="col-xs-12">
                      {optionIndex !== null ? (
                        <Link
                          to={continueLink}
                          type="button"
                          look="accent"
                          size="large"
                          event
                          className="c-button c-button--large c-select-course-option-form__submit"
                        >
                          {t('event.continue')}
                        </Link>
                      ) : (
                        <div className="uppercase c-select-course-option-form__disabled-btn">
                          {t('event.continue')}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }
}

SelectCourseOptionForm.propTypes = {
  onClearBasket: PropTypes.func.isRequired,
  courseOptions: PropTypes.array,
  brand: PropTypes.object,
  ticketCount: PropTypes.number,
  deliveryType: PropTypes.string,
  isMultiCurrency: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    pageConfig: state.pageConfig,
    ticketCount: getTicketCount(state),
  };
}

export default withTranslation()(
  connect(mapStateToProps)(withBrand(withRouter(SelectCourseOptionForm))),
);
